import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Button,
    Tooltip,
    useTheme
} from '@mui/material';
import {
    ExpandMore,
    CropSquare,
    CircleOutlined,
    ChangeHistory,
    ViewColumn,
    ViewStream,
    Window,
    GridOn,
    PanTool,
    Mouse
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// Define tool types to match what DesignCanvas expects (plus shapes)
export type ToolType =
    | 'select'
    | 'splitter-vertical'
    | 'splitter-horizontal'
    | 'sash'
    | 'glass'
    | 'delete'
    | 'shape-rect'
    | 'shape-round'
    | 'shape-trapezoid';

interface DesignToolboxProps {
    activeTool: ToolType;
    onToolChange: (tool: ToolType) => void;
}

const DesignToolbox: React.FC<DesignToolboxProps> = ({ activeTool, onToolChange }) => {
    const { t } = useLanguage();
    const theme = useTheme();

    const renderToolBtn = (tool: ToolType, icon: React.ReactNode, label: string) => (
        <Grid item xs={4}>
            <Tooltip title={label}>
                <Button
                    variant={activeTool === tool ? "contained" : "outlined"}
                    color={activeTool === tool ? "primary" : "inherit"}
                    sx={{
                        minWidth: 'auto',
                        width: '100%',
                        height: 50,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 0.5,
                        borderColor: activeTool === tool ? 'primary.main' : 'divider'
                    }}
                    onClick={() => onToolChange(tool)}
                >
                    {icon}
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.5, lineHeight: 1 }}>
                        {label}
                    </Typography>
                </Button>
            </Tooltip>
        </Grid>
    );

    return (
        <Box sx={{ width: '100%', height: '100%', overflowY: 'auto' }}>
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Button
                    fullWidth
                    variant={activeTool === 'select' ? "contained" : "outlined"}
                    startIcon={<Mouse />}
                    onClick={() => onToolChange('select')}
                    sx={{ mb: 1 }}
                >
                    {t('maker.design.canvas.select')}
                </Button>
            </Box>

            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Shapes</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                    <Grid container spacing={1}>
                        {renderToolBtn('shape-rect', <CropSquare />, t('maker.shape.rectangular'))}
                        {renderToolBtn('shape-round', <CircleOutlined />, t('maker.shape.round'))}
                        {renderToolBtn('shape-trapezoid', <ChangeHistory />, t('maker.shape.trapezoid'))}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Splitters</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                    <Grid container spacing={1}>
                        {renderToolBtn('splitter-vertical', <ViewColumn />, t('maker.design.canvas.vsplit'))}
                        {renderToolBtn('splitter-horizontal', <ViewStream />, t('maker.design.canvas.hsplit'))}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Wings</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                    <Grid container spacing={1}>
                        {renderToolBtn('sash', <Window />, t('maker.design.canvas.sash'))}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Surfaces</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                    <Grid container spacing={1}>
                        {renderToolBtn('glass', <GridOn />, "Glass")}
                        {/* Placeholder for now */}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default DesignToolbox;
